import React, { useEffect, useState, useContext } from "react";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";

const COLORS = ["#f9a825", "#ffb74d", "#9575cd", "#4fc3f7", "#f06292", "#4db6ac"];

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [transactions, setTransactions] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthlyIncome, setMonthlyIncome] = useState(0);
  const [monthlyExpense, setMonthlyExpense] = useState(0);
  const [lastMonthExpense, setLastMonthExpense] = useState(0);
  const [expenseComparisonText, setExpenseComparisonText] = useState("");
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [transactionsRes, userRes] = await Promise.all([
          axios.get("/finance/transactions/"),
          axios.get("/users/me/"),
        ]);

        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
        const lastMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;

        let totalExp = 0;
        let income = 0;
        let expense = 0;
        let lastMonthExp = 0;
        const monthMap = {};
        const categoryMap = {};

        const transformed = transactionsRes.data.map((t) => {
          const dateObj = new Date(t.date);
          const month = dateObj.getMonth();
          const year = dateObj.getFullYear();

          const label = `${dateObj.toLocaleString("default", {
            month: "short",
          })} ${year}`;

          if (t.type === "expense") {
            totalExp += parseFloat(t.amount);
          }

          if (!monthMap[label]) monthMap[label] = 0;
          if (t.type === "expense") {
            monthMap[label] += parseFloat(t.amount);

            const cat = t.category || "Uncategorized";
            categoryMap[cat] = (categoryMap[cat] || 0) + parseFloat(t.amount);
          }

          if (month === currentMonth && year === currentYear) {
            if (t.type === "income") income += parseFloat(t.amount);
            if (t.type === "expense") expense += parseFloat(t.amount);
          }

          if (
            month === lastMonth &&
            year === lastMonthYear &&
            t.type === "expense"
          ) {
            lastMonthExp += parseFloat(t.amount);
          }

          return {
            ...t,
            date: dateObj.toLocaleDateString(),
            time: dateObj.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            }),
          };
        });

        const trendsData = Object.entries(monthMap).map(([month, total]) => ({
          month,
          expense: parseFloat(total.toFixed(2)),
        }));
        trendsData.sort(
          (a, b) => new Date(`01 ${a.month}`) - new Date(`01 ${b.month}`)
        );

        const pieChartData = Object.entries(categoryMap).map(([category, value]) => ({
          name: category,
          value: parseFloat(value.toFixed(2)),
        }));
        pieChartData.sort((a, b) => b.value - a.value);

        setMonthlyTrends(trendsData);
        setPieData(pieChartData);
        setMonthlyIncome(income);
        setMonthlyExpense(expense);
        setLastMonthExpense(lastMonthExp);
        setTransactions(transformed);
        setTotalExpense(totalExp);

        let balance = 0;
        transactionsRes.data.forEach((t) => {
          if (t.type === "income") balance += parseFloat(t.amount);
          if (t.type === "expense") balance -= parseFloat(t.amount);
        });

setUserInfo({ ...userRes.data, balance });


        if (lastMonthExp === 0 && expense > 0) {
          setExpenseComparisonText("No expenses last month");
        } else if (expense === 0 && lastMonthExp > 0) {
          setExpenseComparisonText("No expenses this month");
        } else if (lastMonthExp === 0 && expense === 0) {
          setExpenseComparisonText("No expenses recorded yet");
        } else {
          const diff = ((expense - lastMonthExp) / lastMonthExp) * 100;
          const roundedDiff = Math.abs(diff).toFixed(1);
          const direction = diff >= 0 ? "more" : "less";
          setExpenseComparisonText(`${roundedDiff}% ${direction} than last month`);
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  // Inline SVGs for buttons (small, no extra deps)
  const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );

  const ChatIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );

  // helper: top categories derived from existing pieData (already sorted desc)
  const topCategories = pieData.slice(0, 4);

return (
    <div className="bg-gray-50 min-h-screen">
      <div className="p-6 md:p-8 pt-6">
        <h1 className="text-2xl md:text-3xl font-semibold mb-6 md:mb-8 mt-2">
          Good Day, {userInfo ? userInfo.username : "User"}
        </h1>

        {/* Container: stack on mobile, 3 columns on md+ */}
        <div className="bg-white rounded-2xl shadow-md grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 mt-4">
          {/* Virtual Card (col 1) */}
          <div className="p-4 md:p-6 flex flex-col gap-4 md:min-h-[640px]">
            <h2 className="text-lg md:text-xl font-semibold">Virtual Card</h2>

            {/* Desktop vertical card */}
            <div className="hidden md:block">
              <div
                className="relative rounded-xl text-white p-6 overflow-hidden shadow-2xl"
                style={{
                  background: "linear-gradient(135deg, #6B46C1 0%, #2ecfe3 60%)",
                }}
              >
                <div className="absolute -right-16 -top-10 w-40 h-40 bg-white/6 rounded-full blur-3xl pointer-events-none"></div>
                <div className="absolute -left-10 -bottom-10 w-56 h-56 bg-black/6 rounded-full blur-3xl pointer-events-none"></div>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-7 rounded-md bg-white/80 flex items-center justify-center">
                      <div className="w-6 h-3 bg-gray-200 rounded-sm"></div>
                    </div>
                    <div className="text-sm font-semibold tracking-wide">
                      NeoFinance
                    </div>
                  </div>
                  <div className="text-right text-xs opacity-90">
                    Member since:
                    <div className="font-medium">
                      {userInfo ? formatDate(userInfo.created_at) : "N/A"}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="text-sm opacity-90">
                    Your current balance
                  </div>
                  <div className="text-3xl md:text-4xl font-extrabold leading-tight mt-1">
                    €{userInfo ? parseFloat(userInfo.balance).toFixed(2) : "0.00"}
                  </div>
                </div>
                <div className="flex items-center justify-between mt-6 text-sm opacity-95">
                  <div>
                    <div className="text-xs opacity-90">Income this month</div>
                    <div className="font-medium">€{monthlyIncome.toFixed(2)}</div>
                  </div>
                </div>
                <div className="absolute right-6 bottom-16 text-sm tracking-widest opacity-95">
                  •••• •••• ••••{" "}
                  {userInfo ? String(userInfo.id).slice(-4) : "0000"}
                </div>
                <div className="absolute right-6 bottom-6 text-right">
                  <div className="text-xs opacity-90">Spending</div>
                  <div className="font-semibold ">
                    €{monthlyExpense.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile condensed card */}
            <div className="md:hidden">
              <div
                className="relative rounded-xl text-white p-4 overflow-hidden shadow-lg"
                style={{
                  background: "linear-gradient(135deg, #6B46C1 0%, #2ecfe3 70%)",
                }}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-6 rounded-md bg-white/80 flex items-center justify-center">
                      <div className="w-5 h-2 bg-gray-200 rounded-sm"></div>
                    </div>
                    <div className="text-sm font-semibold">NeoFinance</div>
                  </div>
                  <div className="text-xs opacity-90">Member</div>
                </div>
                <div className="mt-1">
                  <div className="text-xs opacity-90">Balance</div>
                  <div className="text-2xl font-extrabold mt-1">
                    €{userInfo ? parseFloat(userInfo.balance).toFixed(2) : "0.00"}
                  </div>
                </div>
                <div className="mt-3 text-sm tracking-widest opacity-95">
                  •••• •••• ••••{" "}
                  {userInfo ? String(userInfo.id).slice(-4) : "0000"}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs opacity-95">
                  <div>
                    <div className="text-[10px] opacity-80">Income</div>
                    <div className="font-medium text-sm">
                      €{monthlyIncome.toFixed(0)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] opacity-80">Status</div>
                    <div className="text-sm">{expenseComparisonText}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col md:flex-row gap-3">
              <button
                className="flex-1 bg-[#2ecfe3] text-white py-2 px-5 rounded-full hover:bg-[#28bfcf] transform hover:-translate-y-0.5 transition-shadow shadow-md flex items-center justify-center gap-2 text-sm"
                onClick={() => navigate("/transactions")}
              >
                <span className="inline-flex items-center justify-center">
                  <PlusIcon />
                </span>
                <span>Add more funds</span>
              </button>
              <button
                className="flex-1 bg-white text-[#2ecfe3] border border-[#2ecfe3] py-2 px-5 rounded-full hover:bg-[#f7fffe] transition-shadow shadow-md flex items-center justify-center gap-2 text-sm"
                onClick={() => navigate("/assistant")}
              >
                <span className="inline-flex items-center justify-center">
                  <ChatIcon />
                </span>
                <span>Talk about your spendings</span>
              </button>
            </div>

            {/* KPI cards (desktop only) */}
            <div className="hidden md:grid md:grid-cols-3 gap-3 mt-3">
              <div className="col-span-3 bg-white rounded-lg p-3 shadow-sm border">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-500">Top categories</div>
                  <div className="text-xs text-gray-400">
                    Total tracked: €{totalExpense.toFixed(2)}
                  </div>
                </div>
                {topCategories.length === 0 ? (
                  <p className="text-sm text-gray-500">No categories yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {topCategories.map((c, idx) => {
                      const pct =
                        totalExpense > 0 ? (c.value / totalExpense) * 100 : 0;
                      return (
                        <li key={idx} className="py-2.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 min-w-0">
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                style={{
                                  background: COLORS[idx % COLORS.length],
                                }}
                              />
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {c.name}
                              </span>
                            </div>
                            <div className="text-sm font-semibold text-gray-900">
                              €{c.value.toFixed(2)}
                            </div>
                          </div>
                          <div className="mt-2 w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${Math.max(
                                  0,
                                  Math.min(100, pct)
                                ).toFixed(1)}%`,
                                background: COLORS[idx % COLORS.length],
                              }}
                            />
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {pct.toFixed(1)}%
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Transactions (col 2) */}
          <div className="p-4 md:p-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg md:text-xl font-semibold">
                Transactions
              </h2>
              <div className="text-right">
                <p
                  className="text-blue-500 text-sm font-medium cursor-pointer hover:underline mb-1"
                  onClick={() => navigate("/transactions")}
                >
                  See all &gt;
                </p>
              </div>
            </div>
            <div className="space-y-4">
              {loading ? (
                <p className="text-sm text-gray-500">
                  Loading transactions...
                </p>
              ) : transactions.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No transactions yet.{" "}
                  <span
                    className="text-blue-500 cursor-pointer hover:underline"
                    onClick={() => navigate("/transactions")}
                  >
                    Add one now
                  </span>
                  .
                </p>
              ) : (
                transactions.slice(0, 7).map((t, i) => (
                  <div
                    key={i}
                    className="flex items-start justify-between border-b pb-2"
                  >
                    <div className="flex-1 min-w-0 pr-3">
                      <div className="text-xs md:text-sm text-gray-500">
                        {t.date} {t.time}
                      </div>
                      <div className="text-sm md:text-base font-medium truncate">
                        Transaction category: {t.category}
                      </div>
                      <div className="text-xs md:text-sm text-gray-500 truncate">
                        Transaction ID: {t.id}
                      </div>
                    </div>
                    <div
                      className={`w-24 text-sm md:text-lg font-bold flex-shrink-0 text-right ${
                        t.type === "expense"
                          ? "text-red-500"
                          : "text-green-600"
                      }`}
                    >
                      €{parseFloat(t.amount).toFixed(2)}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* START: FIX - Charts (col 3) */}
          {/* This new wrapper div acts as the third column */}
          <div className="p-4 md:p-6 space-y-6">
            {/* Chart 1: Spending Breakdown */}
            <div>
              <h2 className="text-lg font-semibold mb-4">
                Spending Breakdown
              </h2>
                {pieData.length === 0 ? (
                  <div className="h-64 flex flex-col items-center justify-center relative">
                    {/* Faded donut placeholder */}
                    <div className="w-40 h-40 rounded-full border-8 border-dashed border-gray-200 opacity-60"></div>
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
                      <p className="text-sm font-medium">No spending data</p>
                      <p className="text-xs">Add transactions to see breakdown</p>
                    </div>
                  </div>
                ) : (
                <div className="relative h-64">
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={4}
                      >
                        {pieData.map((_, i) => (
                          <Cell
                            key={i}
                            fill={COLORS[i % COLORS.length]}
                            stroke="white"
                            strokeWidth={2}
                          />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(val, name) => [`€${val.toFixed(2)}`, name]}
                        contentStyle={{
                          borderRadius: "10px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-sm text-gray-500">Total</span>
                    <span
                      className="text-lg font-semibold"
                      style={{ color: "#2ecfe3" }}
                    >
                      €{totalExpense.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
              {/* Legend */}
              {pieData.length > 0 && (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  {pieData.slice(0, 6).map((cat, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{
                          backgroundColor: COLORS[i % COLORS.length],
                        }}
                      ></span>
                      <span className="truncate">{cat.name}</span>
                      <span className="ml-auto font-medium">
                        €{cat.value.toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Chart 2: Monthly Spending Trend (Bar Chart) */}
            <div>
              <h2 className="text-lg font-semibold mb-4">
                Monthly Spending Trend
              </h2>
              {monthlyTrends.length === 0 ? (
              <div className="h-64 flex flex-col justify-center px-6">
                <div className="flex items-end gap-2 h-full">
                  {[40, 60, 30, 50, 70].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-gray-200 rounded-md opacity-60"
                      style={{ height: `${h}%` }}
                    ></div>
                  ))}
                </div>
                <p className="text-center text-sm text-gray-500 mt-3">
                  Start adding expenses to see trends
                </p>
              </div>
            ) : (
                <div className="h-64">
                  <ResponsiveContainer>
                    <BarChart
                      data={monthlyTrends}
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="#e5e7eb"
                      />
                      <XAxis
                        dataKey="month"
                        stroke="#6b7280"
                        fontSize={12}
                      />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip
                        formatter={(val) => [`€${val.toFixed(2)}`, "Expense"]}
                        contentStyle={{
                          borderRadius: "10px",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                        }}
                      />
                      <Bar
                        dataKey="expense"
                        radius={[6, 6, 0, 0]}
                        fill="url(#colorSpending)"
                        barSize={28}
                      />
                      <defs>
                        <linearGradient
                          id="colorSpending"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#2ecfe3"
                            stopOpacity={0.9}
                          />
                          <stop
                            offset="95%"
                            stopColor="#6B46C1"
                            stopOpacity={0.8}
                          />
                        </linearGradient>
                      </defs>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
          {/* END: FIX */}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;